"use client";
import React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import AlertDialog from "./AlertDialog";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "12px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "12px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    zIndex: 50,
  }),
};

const multiSelectFilterStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#1f2937",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    minHeight: "50px",
    zIndex: 100000,
    fontSize: "14px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({ ...provided, color: "#e5e7eb" }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#374151",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
    border: "1px solid #4b5563",
    width: "300px",
    maxWidth: "300px",
    minWidth: "260px",
    zIndex: 999999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "380px",
    padding: "8px",
    overflowY: "auto",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "flex-start",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#4b5563",
    borderRadius: "6px",
    margin: 0,
    fontSize: "13px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9ca3af",
    cursor: "pointer",
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRadius: "4px",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
};

export default function WorkOrderCreateV2() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form state
  const [title, setTitle] = useState("");
  const [costCode, setCostCode] = useState("");
  const [woType, setWoType] = useState(null);
  const [union, setUnion] = useState(null);
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [scopeOfWork, setScopeOfWork] = useState("");

  // Data
  const [costCodes, setCostCodes] = useState([]);
  const [unions, setUnions] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  // Filters
  const [filters, setFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    bidArea: [],
    uom: [],
    descriptionSearch: "",
  });

  // UI
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFilter, setShowFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const searchTimeoutRef = useRef(null);
  const [alert, setAlert] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
  });

  const conditionRef = useRef(null);
  const sectionRef = useRef(null);
  const bidAreaRef = useRef(null);
  const uomRef = useRef(null);
  const descriptionRef = useRef(null);

  // Click outside handler (robust for react-select portals)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showFilter) return;

      const refs = {
        conditionName: conditionRef,
        sectionNumber: sectionRef,
        bidArea: bidAreaRef,
        uom: uomRef,
        descriptionSearch: descriptionRef,
      };
      const activeRef = refs[showFilter];

      if (activeRef?.current && activeRef.current.contains(event.target))
        return;

      const clickedInsideReactSelect =
        event.target.closest('[class*="css-"]') ||
        event.target.closest(".react-select__menu") ||
        event.target.closest(".react-select__menu-portal") ||
        event.target.closest(".react-select__option") ||
        (event.target.id && event.target.id.startsWith("react-select"));

      if (clickedInsideReactSelect) return;

      setShowFilter(null);
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const [costRes, unionRes] = await Promise.all([
          axios.get(`${API_BASE}/costCodes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE}/company-profile/unions`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setCostCodes(costRes.data);
        setUnions(unionRes.data);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Load Error",
          message: "Failed to load data",
        });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      if (!id) return;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE}/assemblies/project/${id}/labor?unassignedOnly=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const items = res.data.map((item) => ({
          ...item,
          assemblyId: item.assemblyId,
          lineItemId: item._id,
          description:
            item.costCodeDescription ||
            item.itemDescription ||
            "No Description",
          quantity: item.quantity || 0,
          laborHours: item.laborHours || 0,
          amount: item.amount || 0,
        }));
        setAllItems(items);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Data Error",
          message: "Failed to load labor items",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchItems();
  }, [id]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (
        filters.conditionName.length > 0 &&
        !filters.conditionName.includes(item.conditionName)
      )
        return false;
      if (
        filters.sectionNumber.length > 0 &&
        !filters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (filters.bidArea.length > 0 && !filters.bidArea.includes(item.bidArea))
        return false;
      if (filters.uom.length > 0 && !filters.uom.includes(item.uom))
        return false;
      if (filters.descriptionSearch) {
        const desc = (item.description || "").toLowerCase();
        if (!desc.includes(filters.descriptionSearch.toLowerCase()))
          return false;
      }
      return true;
    });
  }, [allItems, filters]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * 100;
    return filteredItems.slice(start, start + 100);
  }, [filteredItems, currentPage]);

  const groupedItems = useMemo(() => {
    const groups = {};
    paginatedItems.forEach((item) => {
      const key = item.conditionName || "Uncategorized";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [paginatedItems]);

  const selectedItems = useMemo(() => {
    return filteredItems.filter((item) => selectedItemIds.has(item.lineItemId));
  }, [filteredItems, selectedItemIds]);

  const totals = useMemo(() => {
    return selectedItems.reduce(
      (acc, item) => ({
        count: acc.count + 1,
        hours: acc.hours + (item.laborHours || 0),
        amount: acc.amount + (item.amount || 0),
      }),
      { count: 0, hours: 0, amount: 0 }
    );
  }, [selectedItems]);

  const filteredTotals = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => ({
        count: acc.count + 1,
        hours: acc.hours + (item.laborHours || 0),
        amount: acc.amount + (item.amount || 0),
      }),
      { count: 0, hours: 0, amount: 0 }
    );
  }, [filteredItems]);

  const filterOptions = useMemo(() => {
    return {
      conditionName: [
        ...new Set(allItems.map((i) => i.conditionName).filter(Boolean)),
      ].sort(),
      sectionNumber: [
        ...new Set(allItems.map((i) => i.sectionNumber).filter(Boolean)),
      ].sort(),
      bidArea: [
        ...new Set(allItems.map((i) => i.bidArea).filter(Boolean)),
      ].sort(),
      uom: [...new Set(allItems.map((i) => i.uom).filter(Boolean))].sort(),
    };
  }, [allItems]);

  const hasActiveFilters = Object.values(filters).some((f) =>
    Array.isArray(f) ? f.length > 0 : f
  );

  const toggleFilter = (field) =>
    setShowFilter((prev) => (prev === field ? null : field));

  const handleFilterChange = (type, value) => {
    if (type === "descriptionSearch") {
      setFilters((prev) => ({ ...prev, descriptionSearch: value }));
    } else {
      setFilters((prev) => ({ ...prev, [type]: value || [] }));
    }
  };

  const handleDescriptionSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(
      () => handleFilterChange("descriptionSearch", value),
      300
    );
  };

  const clearAllFilters = () => {
    setFilters({
      conditionName: [],
      sectionNumber: [],
      bidArea: [],
      uom: [],
      descriptionSearch: "",
    });
    setSearchInput("");
    setSelectedItemIds(new Set());
    setCurrentPage(1);
  };

  const handleSelectAllFiltered = () => {
    if (selectedItemIds.size === filteredItems.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(filteredItems.map((i) => i.lineItemId)));
    }
  };

  const allFilteredSelected =
    selectedItemIds.size === filteredItems.length && filteredItems.length > 0;

  const handleItemSelect = (item) => {
    setSelectedItemIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(item.lineItemId)) newSet.delete(item.lineItemId);
      else newSet.add(item.lineItemId);
      return newSet;
    });
  };

  const formatCurrency = (num) =>
    `$${Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
    })}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation",
        message: "Select at least one labor item",
      });
      return;
    }
    if (!title || !costCode || !woType) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation",
        message: "Title, Cost Code, and Type are required",
      });
      return;
    }
    if (woType === "subcontractor" && (!scopeOfWork || !termsAndConditions)) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation",
        message: "Scope and Terms required",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const itemsToAdd = selectedItems.map((i) => ({
        assemblyId: i.assemblyId,
        lineItemId: i.lineItemId,
      }));

      await axios.post(
        `${API_BASE}/workorders2`,
        {
          title,
          type: woType,
          project: id,
          costCode,
          union: union || "non-union",
          termsAndConditions: termsAndConditions || "",
          scopeOfWork: scopeOfWork || "",
          items: itemsToAdd,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Work Order created successfully!",
        onConfirm: () =>
          navigate(`/Projects/${id}`, { state: { initialTab: "Work Orders" } }),
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to create Work Order",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading)
    return <div className="text-center py-12 text-gray-200">Loading...</div>;

  return (
    <div className="mx-auto px-4 py-6">
      <AlertDialog
        {...alert}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={
          alert.onConfirm || (() => setAlert({ ...alert, open: false }))
        }
      />

      {/* Header */}
      <div className="mb-6 shadow-xl bg-gray-700 rounded-lg p-6 border border-gray-600">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">
              Create Work Order
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {totals.count} items selected • Estimated Total:{" "}
              {formatCurrency(totals.amount)}
            </p>
          </div>
          <button
            onClick={() => navigate(`/Projects/${id}`)}
            className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Work Order Details */}
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">
            Work Order Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Cost Code *
              </label>
              <Select
                options={costCodes.map((c) => ({
                  value: c._id,
                  label: c.code,
                }))}
                value={costCodes
                  .map((c) => ({ value: c._id, label: c.code }))
                  .find((o) => o.value === costCode)}
                onChange={(o) => setCostCode(o?.value || "")}
                styles={customStyles}
                placeholder="Select..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Type *
              </label>
              <Select
                options={[
                  { value: "hourly", label: "Hourly" },
                  { value: "piecework", label: "Piecework" },
                  { value: "subcontractor", label: "Subcontractor" },
                ]}
                value={[
                  { value: "hourly", label: "Hourly" },
                  { value: "piecework", label: "Piecework" },
                  { value: "subcontractor", label: "Subcontractor" },
                ].find((o) => o.value === woType)}
                onChange={(o) => setWoType(o?.value)}
                styles={customStyles}
                placeholder="Select type..."
              />
            </div>
            {(woType === "hourly" || woType === "piecework") && (
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Union
                </label>
                <Select
                  options={[
                    { value: "non-union", label: "Non-Union" },
                    ...unions.map((u) => ({ value: u.title, label: u.title })),
                  ]}
                  value={[
                    { value: "non-union", label: "Non-Union" },
                    ...unions.map((u) => ({ value: u.title, label: u.title })),
                  ].find((o) => o.value === union)}
                  onChange={(o) => setUnion(o?.value)}
                  styles={customStyles}
                />
              </div>
            )}
            {woType === "subcontractor" && (
              <>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-400">
                    Scope of Work *
                  </label>
                  <textarea
                    value={scopeOfWork}
                    onChange={(e) => setScopeOfWork(e.target.value)}
                    rows={4}
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500"
                    required
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-400">
                    Terms & Conditions *
                  </label>
                  <textarea
                    value={termsAndConditions}
                    onChange={(e) => setTermsAndConditions(e.target.value)}
                    rows={4}
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500"
                    required
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Labor Items */}
        <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 shadow-xl">
          <h2 className="text-lg font-semibold text-white mb-4">
            Select Labor Items
          </h2>

          {/* Control Bar */}
          <div className="bg-gray-800 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4 text-xl">
                <span className="text-gray-400">
                  <span className="text-white font-semibold">
                    {filteredItems.length.toLocaleString()}
                  </span>
                  {filteredItems.length !== allItems.length && (
                    <span className="text-gray-500">
                      {" "}
                      / {allItems.length.toLocaleString()}
                    </span>
                  )}{" "}
                  items
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-orange-400 font-medium">
                  {formatCurrency(filteredTotals.amount)}
                </span>
                <span className="text-gray-600">|</span>
                <span className="text-blue-400">
                  Hours: {filteredTotals.hours.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectAllFiltered}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-600"
                >
                  {allFilteredSelected ? "Deselect All" : "Select All"}
                </button>
                <span className="text-gray-400 text-sm">
                  {selectedItems.length} selected
                </span>
              </div>
            </div>

            {/* Filter Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleDescriptionSearchChange}
                  placeholder="Search..."
                  className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      handleFilterChange("descriptionSearch", "");
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className="text-gray-600">|</span>

              {filters.conditionName.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                  Assembly:{" "}
                  {filters.conditionName.length === 1
                    ? filters.conditionName[0].substring(0, 15) +
                      (filters.conditionName[0].length > 15 ? "…" : "")
                    : `${filters.conditionName.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("conditionName", [])}
                    className="hover:text-teal-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.sectionNumber.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-blue-600/80 text-white rounded">
                  Section:{" "}
                  {filters.sectionNumber.length === 1
                    ? filters.sectionNumber[0]
                    : `${filters.sectionNumber.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("sectionNumber", [])}
                    className="hover:text-blue-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.bidArea.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                  Area:{" "}
                  {filters.bidArea.length === 1
                    ? filters.bidArea[0]
                    : `${filters.bidArea.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("bidArea", [])}
                    className="hover:text-green-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.uom.length > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                  UOM:{" "}
                  {filters.uom.length === 1
                    ? filters.uom[0]
                    : `${filters.uom.length} selected`}
                  <button
                    type="button"
                    onClick={() => handleFilterChange("uom", [])}
                    className="hover:text-purple-200"
                  >
                    ×
                  </button>
                </span>
              )}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-[13px]">
              <thead className="bg-gray-800/95 sticky top-0 z-10">
                <tr className="border-b border-gray-700">
                  <th className="w-8 px-1 py-1.5 text-center text-[13px] font-medium text-gray-400">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={handleSelectAllFiltered}
                      className="h-3 w-3 rounded border-gray-500 text-orange-500"
                    />
                  </th>
                  <th
                    className={`w-96 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                      filters.conditionName.length > 0
                        ? "text-orange-400"
                        : "text-gray-400"
                    }`}
                    onClick={() => toggleFilter("conditionName")}
                  >
                    <span className="flex items-center gap-0.5">
                      Assembly ↓{" "}
                      {filters.conditionName.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {filters.conditionName.length}
                        </span>
                      )}
                    </span>
                    {showFilter === "conditionName" && (
                      <div
                        ref={conditionRef}
                        className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                        style={{ minWidth: "300px" }}
                      >
                        <div className="p-2">
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={filterOptions.conditionName.map((c) => ({
                              value: c,
                              label: c,
                            }))}
                            value={filterOptions.conditionName
                              .map((c) => ({ value: c, label: c }))
                              .filter((o) =>
                                filters.conditionName.includes(o.value)
                              )}
                            onChange={(opts) =>
                              handleFilterChange(
                                "conditionName",
                                opts ? opts.map((o) => o.value) : []
                              )
                            }
                            menuShouldBlockScroll={true}
                            menuPortalTarget={document.body}
                            styles={multiSelectFilterStyles}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th
                    className={`w-32 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                      filters.sectionNumber.length > 0
                        ? "text-orange-400"
                        : "text-gray-400"
                    }`}
                    onClick={() => toggleFilter("sectionNumber")}
                  >
                    <span className="flex items-center gap-0.5">
                      Section ↓{" "}
                      {filters.sectionNumber.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {filters.sectionNumber.length}
                        </span>
                      )}
                    </span>
                    {showFilter === "sectionNumber" && (
                      <div
                        ref={sectionRef}
                        className="absolute left-0 top-full z-50 mt-1 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                        style={{ minWidth: "200px" }}
                      >
                        <div className="p-2">
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            options={filterOptions.sectionNumber.map((s) => ({
                              value: s,
                              label: s,
                            }))}
                            value={filterOptions.sectionNumber
                              .map((s) => ({ value: s, label: s }))
                              .filter((o) =>
                                filters.sectionNumber.includes(o.value)
                              )}
                            onChange={(opts) =>
                              handleFilterChange(
                                "sectionNumber",
                                opts ? opts.map((o) => o.value) : []
                              )
                            }
                            menuShouldBlockScroll={true}
                            menuPortalTarget={document.body}
                            styles={multiSelectFilterStyles}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th
                    className={`min-w-[200px] px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                      filters.descriptionSearch
                        ? "text-orange-400"
                        : "text-gray-400"
                    }`}
                    onClick={() => toggleFilter("descriptionSearch")}
                  >
                    <span className="flex items-center gap-0.5">
                      Description 🔍
                    </span>
                    {showFilter === "descriptionSearch" && (
                      <div
                        ref={descriptionRef}
                        className="absolute z-20 mt-1 bg-gray-700 border border-gray-600 rounded shadow-xl w-56"
                      >
                        <div className="p-1.5">
                          <input
                            type="text"
                            value={searchInput}
                            onChange={handleDescriptionSearchChange}
                            placeholder="Type to search..."
                            className="w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-[13px] py-1 px-2 focus:outline-none focus:border-orange-500"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th
                    className={`w-20 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                      filters.bidArea.length > 0
                        ? "text-orange-400"
                        : "text-gray-400"
                    }`}
                    onClick={() => toggleFilter("bidArea")}
                  >
                    <span className="flex items-center gap-0.5">
                      Bid Area ↓{" "}
                      {filters.bidArea.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {filters.bidArea.length}
                        </span>
                      )}
                    </span>
                    {showFilter === "bidArea" && (
                      <div
                        ref={bidAreaRef}
                        className="absolute left-0 top-full mt-1 z-[999999]"
                      >
                        <div
                          className="bg-gray-700 border border-gray-500 rounded-lg shadow-2xl overflow-hidden"
                          style={{ width: "320px" }}
                        >
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            options={filterOptions.bidArea.map((b) => ({
                              value: b,
                              label: b,
                            }))}
                            value={filterOptions.bidArea
                              .map((b) => ({ value: b, label: b }))
                              .filter((o) => filters.bidArea.includes(o.value))}
                            onChange={(opts) =>
                              handleFilterChange(
                                "bidArea",
                                opts ? opts.map((o) => o.value) : []
                              )
                            }
                            menuShouldBlockScroll={true}
                            menuPortalTarget={document.body}
                            styles={multiSelectFilterStyles}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                    Qty
                  </th>
                  <th
                    className={`w-16 px-1 py-1.5 text-left text-[13px] font-medium cursor-pointer relative hover:text-orange-300 ${
                      filters.uom.length > 0
                        ? "text-orange-400"
                        : "text-gray-400"
                    }`}
                    onClick={() => toggleFilter("uom")}
                  >
                    <span className="flex items-center gap-0.5">
                      UOM ↓{" "}
                      {filters.uom.length > 0 && (
                        <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                          {filters.uom.length}
                        </span>
                      )}
                    </span>
                    {showFilter === "uom" && (
                      <div
                        ref={uomRef}
                        className="absolute left-0 top-full mt-1 z-50 bg-gray-700 border border-gray-500 rounded-lg shadow-2xl"
                        style={{ minWidth: "180px" }}
                      >
                        <div className="p-2">
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            options={filterOptions.uom.map((u) => ({
                              value: u,
                              label: u,
                            }))}
                            value={filterOptions.uom
                              .map((u) => ({ value: u, label: u }))
                              .filter((o) => filters.uom.includes(o.value))}
                            onChange={(opts) =>
                              handleFilterChange(
                                "uom",
                                opts ? opts.map((o) => o.value) : []
                              )
                            }
                            menuShouldBlockScroll={true}
                            menuPortalTarget={document.body}
                            styles={multiSelectFilterStyles}
                          />
                        </div>
                      </div>
                    )}
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                    Hours
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-400 uppercase rounded-tr-lg">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {Object.entries(groupedItems).map(([condition, items]) => (
                  <React.Fragment key={condition}>
                    <tr className="bg-gray-800/80">
                      <td
                        colSpan="9"
                        className="px-4 py-2 text-sm font-medium text-white"
                      >
                        <span className="flex items-center justify-between">
                          <span>{condition}</span>
                          <span className="text-gray-400 text-xs">
                            {items.length} items
                          </span>
                        </span>
                      </td>
                    </tr>
                    {items.map((item, idx) => {
                      const isSelected = selectedItemIds.has(item.lineItemId);
                      return (
                        <tr
                          key={item.lineItemId}
                          className={`hover:bg-gray-700/50 ${
                            idx % 2 === 0 ? "bg-gray-800/30" : "bg-gray-400/5"
                          } ${
                            isSelected
                              ? "ring-1 ring-orange-500/50 bg-orange-900/20"
                              : ""
                          }`}
                        >
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleItemSelect(item)}
                              className="h-4 w-4 text-orange-400 rounded border-gray-500"
                            />
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-300">
                            {item.conditionName || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-white">
                            {item.sectionNumber || "-"}
                          </td>
                          <td
                            className="px-2 py-2 text-sm text-gray-200 max-w-[220px] truncate"
                            title={item.description}
                          >
                            {item.description}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.bidArea || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-white font-medium">
                            {item.quantity || 0}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-200">
                            {item.uom || "-"}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-orange-400 font-medium">
                            {item.laborHours || 0}
                          </td>
                          <td className="px-2 py-2 text-sm text-right text-white">
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-12 text-center text-gray-400"
                    >
                      No items matching filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {Math.ceil(filteredItems.length / 100) > 1 && (
            <div className="flex items-center justify-center gap-2 py-4 mt-2">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-gray-400 rounded disabled:opacity-30 hover:bg-gray-600"
              >
                ««
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
              >
                ‹ Prev
              </button>
              <span className="text-[13px] text-white">
                Page {currentPage} of {Math.ceil(filteredItems.length / 100)}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((p) =>
                    Math.min(Math.ceil(filteredItems.length / 100), p + 1)
                  )
                }
                disabled={currentPage === Math.ceil(filteredItems.length / 100)}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
              >
                Next ›
              </button>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage(Math.ceil(filteredItems.length / 100))
                }
                disabled={currentPage === Math.ceil(filteredItems.length / 100)}
                className="px-2 py-1 text-[13px] bg-gray-700 text-white rounded disabled:opacity-30 hover:bg-gray-600"
              >
                »»
              </button>
            </div>
          )}

          {/* Summary */}
          {totals.count > 0 && (
            <div className="mt-4 p-4 bg-gray-800 rounded-lg">
              <h3 className="text-md font-semibold text-gray-400 mb-2">
                Selection Summary
              </h3>
              <div className="grid grid-cols-3 gap-4 text-gray-200">
                <div>
                  <span className="text-gray-400">Items:</span> {totals.count}
                </div>
                <div>
                  <span className="text-gray-400">Total Hours:</span>{" "}
                  {totals.hours.toFixed(1)}
                </div>
                <div>
                  <span className="text-gray-400">Estimated Total:</span>{" "}
                  <span className="text-orange-400 font-semibold">
                    {formatCurrency(totals.amount)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/Projects/${id}`)}
            className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || totals.count === 0}
            className={`px-4 py-2 text-sm text-white rounded-md ${
              isSubmitting || totals.count === 0
                ? "bg-orange-300 cursor-not-allowed"
                : "bg-orange-400 hover:bg-orange-500"
            }`}
          >
            {isSubmitting ? "Creating..." : "Create Work Order"}
          </button>
        </div>
      </form>
    </div>
  );
}
